import { useMemo, useState } from "react";
import ReactSelect, { type StylesConfig } from "react-select";
import { customSelectStyles } from "@/components/FederalSelect";
import { groupBy } from "@/lib/utils";

export interface CaseTypeOption {
  label: string | null;
  value: string | null;
  key: string;
}

type Option = { label: string; value: string | null };
type Group = { label: string; options: Option[] };

interface Props {
  options: CaseTypeOption[];
  selected?: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
}

const caseTypeSelectStyles: StylesConfig<Option, false, Group> = {
  ...customSelectStyles,
  control: (baseStyles, state) => ({
    ...customSelectStyles.control?.(baseStyles, state),
    backgroundColor: state.isDisabled ? "#f2f3f3" : "white",
  }),
};

export default function Select(props: Props) {
  const [inputValue, setInputValue] = useState("");

  const selectedOption = useMemo(() => {
    return props.options.find((o) => o.value === props.selected);
  }, [props.selected, props.options]);

  // First, create all transformed options (unfiltered)
  const allTransformedOptions: Group[] = useMemo(() => {
    const group = groupBy(props.options, (i) => i.key);
    const opts = [];

    for (const k in group) {
      opts.push({
        label: k,
        options: group[k].map((v) => ({
          label: v.label ?? "",
          value: v.value,
        })),
      });
    }

    return opts;
  }, [props.options]);

  // Filter options based on input value
  // If group label matches, include ALL options in that group
  const transformedOptions: Group[] = useMemo(() => {
    const searchValue = inputValue.toLowerCase().trim();

    if (!searchValue) {
      return allTransformedOptions;
    }

    const filtered: Group[] = [];

    for (const group of allTransformedOptions) {
      const groupLabelMatches = group.label.toLowerCase().includes(searchValue);

      if (groupLabelMatches) {
        // If group label matches, include ALL options in this group
        filtered.push(group);
      } else {
        // Otherwise, filter options within the group
        const matchingOptions = group.options.filter((option) =>
          option.label.toLowerCase().includes(searchValue),
        );

        // Only include the group if it has matching options
        if (matchingOptions.length > 0) {
          filtered.push({
            label: group.label,
            options: matchingOptions,
          });
        }
      }
    }

    return filtered;
  }, [allTransformedOptions, inputValue]);

  const formatGroupLabel = (data: Group) => {
    return <div>{data.label}</div>;
  };

  const handleChange = (option: Option | null) => {
    props.onChange(option?.value ?? "");
    setInputValue(""); // Clear search after selection
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  return (
    <ReactSelect
      className="select"
      id="case-type"
      options={transformedOptions}
      styles={caseTypeSelectStyles}
      isDisabled={props.isDisabled}
      value={
        selectedOption?.label
          ? { label: selectedOption?.label, value: selectedOption?.value }
          : null
      }
      onChange={handleChange}
      formatGroupLabel={formatGroupLabel}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      filterOption={() => true} // We handle filtering ourselves
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: "#074f6b",
          primary25: "#efefea",
        },
      })}
      placeholder="Required please select..."
      menuPlacement="auto"
    />
  );
}
